import { useEffect, useRef } from "react";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useNavigationState } from "@/zustand/NavigationState";
import { useRtfInputForChat } from "@/hooks/useRtfInputForChat";
import {
  getMediaAttachmentFromLocalDB,
  getFileAttachmentFromLocalDB,
} from "@/indexedDB/services/attachments";
import { EMPTY_PLACEHOLDER_FILE } from "@/config/constants";
import { toMarkdown } from "@/lib/markdown";

const {
  setNewMediaAttachmentId,
  setNewMediaThumbnailId,
  setNewMediaAttachment,
  setMediaAttachmentFileType,
  setThumbnailBlob,
  setNewMediaAttachmentUrl,
  discardMediaAttachment,
} = useMediaMessageSenderState.getState();

const { setNewFileAttachment, setNewFileAttachmentId, discardFileAttachment } =
  useFileMessageSenderState.getState();

interface UseRtfStateOptions {
  setContent: (content: string) => void;
  focus: () => void;
  isEmpty: boolean;
}

export function useRtfState({
  setContent,
  focus,
  isEmpty,
}: UseRtfStateOptions) {
  const activeChatId = useNavigationState((state) => state.activeChatId);

  // Keep a staged attachment tied to the composer that picked it. The sender
  // stores are module-global, so without this a file picked in one place can be
  // sent from another.
  //
  // Not a hard invariant: the edit-hydration effect below resolves its
  // IndexedDB read through `hydratedEditIdRef`, which is per-instance, so a
  // dead instance's `.then()` can still write into the stores just after these
  // discards run. Pre-existing, and the window is a single row read right after
  // a context-menu Edit — but it means this covers navigation, not every
  // possible write.
  //
  // The body alone is not enough. It only runs while this component is alive to
  // observe the change, and the composer does not survive every navigation:
  // `startAIChatCreation()` sets `activeChatId: null` and `CREATE_AI_CHAT` in one
  // update, so MiddleColumn swaps ChatPlayground (which owns RtfInput, which owns
  // this hook) out for NewAIChatForm in the same commit. React runs cleanups on
  // unmount and nothing else — with no cleanup here, nothing discarded, and the
  // file reappeared in the AI composer as though it had been picked there.
  //
  // Chat-to-chat was always fine because MiddleColumn keys ChatPlayground on
  // activeChatId, so the remount re-runs the body. The cleanup covers the case
  // where there is no remount.
  //
  // Both run on a chat switch (cleanup, then body). That is harmless — each
  // discard just resets its store to the initial state.
  useEffect(() => {
    discardMediaAttachment();
    discardFileAttachment();
    return () => {
      discardMediaAttachment();
      discardFileAttachment();
    };
  }, [activeChatId]);

  const { replyMessage, editMessage, draftMessage } =
    useRtfInputForChat(activeChatId);

  // Use refs for callbacks to keep the effect dependency array stable
  const setContentRef = useRef(setContent);
  setContentRef.current = setContent;
  const focusRef = useRef(focus);
  focusRef.current = focus;

  // Track which editMessage.id we've already hydrated so we only do it once
  const hydratedEditIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeChatId) {
      return;
    }

    if (!editMessage) {
      // An edit that WAS in progress has ended — cancelled via the chip's X,
      // superseded by another edit, or sent. Drop whatever it staged.
      //
      // Hydration below stages a genuine 0-byte File as a placeholder whenever
      // the blob is not inline on the row, and that is the common case rather
      // than an edge one: getFileAttachmentFromLocalDB is a raw row read, and
      // large files are routed to OPFS with `content: undefined`. Clearing the
      // edit does not clear the stores, so before this the placeholder outlived
      // the cancel and the next ordinary send was refused as an empty file —
      // naming an attachment the user never knowingly picked, after the
      // composer had already been cleared.
      //
      // Guarded on the ref rather than running whenever there is no edit: this
      // effect also re-runs on `isEmpty`, so an unguarded discard would wipe a
      // normally staged attachment as soon as the user typed.
      if (hydratedEditIdRef.current !== null) {
        discardFileAttachment();
        discardMediaAttachment();
      }
      hydratedEditIdRef.current = null;
    }

    if (editMessage && hydratedEditIdRef.current !== editMessage.id) {
      hydratedEditIdRef.current = editMessage.id;
      const hydratingId = editMessage.id;

      // Convert content to markdown for editing (handles both HTML and markdown)
      const markdownContent = toMarkdown(editMessage.content.text);
      setContentRef.current(markdownContent);

      // Initialize attachment state when editing a message with attachment
      if (editMessage.hasAttachment) {
        const { media, file } = editMessage.content;

        if (media) {
          setNewMediaAttachmentId(media.attachmentId);
          if (media.attachmentThumbnailId) {
            setNewMediaThumbnailId(media.attachmentThumbnailId);
          }

          getMediaAttachmentFromLocalDB(media.attachmentId)
            .then((attachment) => {
              if (hydratedEditIdRef.current !== hydratingId) {
                return;
              }
              if (attachment) {
                const blobToUse = attachment.content || attachment.thumbnail;
                if (blobToUse) {
                  const mediaFile = new File([blobToUse], media.filename, {
                    type: media.fileType,
                  });
                  setNewMediaAttachment(mediaFile);

                  const thumbnailBlob = attachment.thumbnail;
                  if (thumbnailBlob) {
                    setThumbnailBlob(thumbnailBlob);
                  }
                  setMediaAttachmentFileType(
                    media.fileType.split("/")[0] as "image" | "video",
                  );

                  const url = URL.createObjectURL(blobToUse);
                  setNewMediaAttachmentUrl(url);
                } else {
                  setNewMediaAttachment(EMPTY_PLACEHOLDER_FILE);
                }
              } else {
                setNewMediaAttachment(EMPTY_PLACEHOLDER_FILE);
              }
            })
            .catch(() => {
              if (hydratedEditIdRef.current !== hydratingId) {
                return;
              }
              setNewMediaAttachment(EMPTY_PLACEHOLDER_FILE);
            });
        }

        if (file) {
          getFileAttachmentFromLocalDB(file.attachmentId)
            .then((attachment) => {
              if (hydratedEditIdRef.current !== hydratingId) {
                return;
              }
              if (attachment && attachment.content) {
                const documentFile = new File(
                  [attachment.content],
                  file.filename,
                  { type: file.fileType },
                );
                setNewFileAttachment(documentFile);
                setNewFileAttachmentId(file.attachmentId);
              } else {
                const placeholderFile = new File(
                  [new Blob([""], { type: file.fileType })],
                  file.filename,
                  { type: file.fileType },
                );
                setNewFileAttachment(placeholderFile);
                setNewFileAttachmentId(file.attachmentId);
              }
            })
            .catch(() => {
              if (hydratedEditIdRef.current !== hydratingId) {
                return;
              }
              const placeholderFile = new File(
                [new Blob([""], { type: file.fileType })],
                file.filename,
                { type: file.fileType },
              );
              setNewFileAttachment(placeholderFile);
              setNewFileAttachmentId(file.attachmentId);
            });
        }
      }

      focusRef.current();
    } else if (replyMessage) {
      focusRef.current();
    } else if (draftMessage) {
      if (isEmpty) {
        setContentRef.current(draftMessage);
      }
      focusRef.current();
    }
  }, [activeChatId, editMessage, draftMessage, replyMessage, isEmpty]);
}
